import { connect } from 'react-redux';
import { ComponentClass } from 'react';
import MatchList from '../../match-list';
import { AppState } from '@om-fe/core/core/types';
import { areTournamentMatchListTopicPropsEqual, getTournamentMatchListSliceName } from './tournament-match-list.utils';
import {
    getTournamentMatchList,
    getTournamentGroupsOverviewMatchList,
    removeTournamentMatchList,
    removeTournamentGroupsOverviewMatchList,
    updateTournamentMatchList,
    updateTournamentGroupsOverviewMatchList,
} from './tournament-match-list.actions';
import { TournamentMatchListStoreModel } from './tournament-match-list.constants';
import { compose } from 'redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import { MatchListCommon } from '../../utils/match-list-models';
import MatchListSkeleton from '../../match-list-skeleton';
import { MatchListItemLayout, DEFAULT_DISPLAYED_NUMBER_OF_MATCHES } from '../../utils/match-list.constants';
import difference from 'lodash/difference';
import { FilteredMarketGroupsOverview } from '../../utils/match-list-models';
import { PlatformTypes } from '@om-fe/utils';

export interface TournamentMatchListPublicProps extends MatchListCommon {
    readonly tournamentId: string;
    readonly locationId?: string;
    readonly eventCategoryId?: string;
    readonly isDefault?: boolean;
    readonly itemLayout?: MatchListItemLayout;
    readonly groupOverview?: string;
    readonly hasMarketSelector?: boolean;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly preventRedirect?: boolean;
    readonly disciplineId?: string;
    readonly isVirtual?: boolean;
    readonly extraParam?: string;
    readonly isAuthenForTopic?: boolean;
}

interface TournamentMatchListStoreProps {
    readonly sliceKey: string;
    readonly ready: boolean;
    readonly isAuthenticated: boolean;
}

const TournamentMatchList: ComponentClass<TournamentMatchListPublicProps> = compose<ComponentClass<TournamentMatchListPublicProps>>(
    preLoader<TournamentMatchListPublicProps>({
        preload({ props, dispatch }) {
            const { instanceId, tournamentId, liveStatus, isDefault, mainMarketsLimit, groupOverview, hasMarketSelector, extraParam, isAuthenForTopic } = props;
            if (hasMarketSelector && groupOverview) {
                dispatch(getTournamentGroupsOverviewMatchList(instanceId, tournamentId, liveStatus, isDefault, groupOverview, extraParam, isAuthenForTopic));
            } else {
                dispatch(getTournamentMatchList(instanceId, tournamentId, liveStatus, isDefault, mainMarketsLimit, isAuthenForTopic));
            }
        },
        getReadySlice({ instanceId }: TournamentMatchListPublicProps): string {
            const slice: string = getTournamentMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { instanceId, tournamentId, liveStatus, isDefault, mainMarketsLimit, limitNumberOfEvents, groupOverview, hasMarketSelector, extraParam, isAuthenForTopic } = props;
            const limit: number = limitNumberOfEvents || DEFAULT_DISPLAYED_NUMBER_OF_MATCHES;

            if (hasMarketSelector) {
                if (instanceId !== prevProps.instanceId || groupOverview !== prevProps.groupOverview || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                    if (difference(groupOverview.split(','), prevProps.groupOverview.split(',')).length !== 0 || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                        dispatch(removeTournamentGroupsOverviewMatchList(prevProps.instanceId, true));
                    }

                    dispatch(getTournamentGroupsOverviewMatchList(instanceId, tournamentId, liveStatus, isDefault, groupOverview, extraParam, isAuthenForTopic));
                } else if (extraParam !== prevProps.extraParam) {
                    dispatch(removeTournamentGroupsOverviewMatchList(prevProps.instanceId));
                    dispatch(getTournamentGroupsOverviewMatchList(instanceId, tournamentId, liveStatus, isDefault, groupOverview, extraParam, isAuthenForTopic));
                } else if (!areTournamentMatchListTopicPropsEqual(prevProps, props)) {
                    dispatch(updateTournamentGroupsOverviewMatchList(instanceId, tournamentId, liveStatus, isDefault, groupOverview, extraParam, isAuthenForTopic));
                }
            } else {
                if (instanceId !== prevProps.instanceId || isAuthenForTopic !== prevProps.isAuthenForTopic) {
                    dispatch(removeTournamentMatchList(prevProps.instanceId));
                    dispatch(getTournamentMatchList(instanceId, tournamentId, liveStatus, isDefault, mainMarketsLimit, isAuthenForTopic));
                } else if (!areTournamentMatchListTopicPropsEqual(prevProps, props)) {
                    dispatch(updateTournamentMatchList(instanceId, tournamentId, liveStatus, isDefault, mainMarketsLimit, isAuthenForTopic));
                }
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId, hasMarketSelector } = props;
            hasMarketSelector
            ? dispatch(removeTournamentGroupsOverviewMatchList(instanceId))
            : dispatch(removeTournamentMatchList(instanceId));
        },
        loadingAnimation: MatchListSkeleton,
    }),
    connect<TournamentMatchListStoreProps, undefined, TournamentMatchListPublicProps>(
        (state: AppState, props: TournamentMatchListPublicProps): TournamentMatchListStoreProps => {
            const { instanceId } = props;
            const sliceKey: string = getTournamentMatchListSliceName(instanceId);

            const slice: TournamentMatchListStoreModel | undefined = state[sliceKey];
            const { session: { info } } = state;
            const isAuthenticated: boolean = (info && info.isAuthenticated) || false;

            if (!slice) {
                return {
                    sliceKey,
                    ready: false,
                    isAuthenticated,
                };
            }

            return {
                sliceKey,
                ready: slice.ready,
                isAuthenticated,
            };
        },
    ),
)(MatchList);
export default TournamentMatchList;
